/* @ts-ignore */
import queryStringHandler from "query-string-handler";
import { scrollToElement } from "@/components/global/scroll-to-element";

interface ResultItem {
  categoryPage?: CategoryPageTypes;
  featuredImage?: ResultItemImage;
  title?: string;
  readingTime?: string;
  contentTypeDisplay?: string;
  url: string;
}
interface ResultItemImage {
  url?: string | undefined;
  altText?: string;
}

interface CategoryPageTypes {
  url?: string;
  categoryName?: string;
}

export default function BlogIndexPage() {
  const blogIndex = document.querySelector<HTMLDivElement>(".js-blog-index");
  const filtersMenu = document.querySelector<HTMLDivElement>(".js-filters-mobile-menu");
  const fixedMenuWrapper = document.querySelector<HTMLDivElement>(".js-fixed-menu");

  if (blogIndex) {
    const excContentId = blogIndex.getAttribute("data-exc-id") || "";
    const selectFilters = blogIndex.querySelectorAll<HTMLLIElement>(".js-filter-item");
    const resultsWrapper = blogIndex.querySelector<HTMLDivElement>(".js-results-wrapper");
    const selectFilterBtns = blogIndex.querySelectorAll<HTMLButtonElement>(".js-filter-btn");
    const resultsList = blogIndex.querySelector<HTMLDivElement>(".js-results-list");
    const prevPageBtn = blogIndex.querySelector<HTMLButtonElement>(".js-prev-btn");
    const nextPageBtn = blogIndex.querySelector<HTMLButtonElement>(".js-next-btn");
    const clearBtns = blogIndex.querySelectorAll<HTMLButtonElement>(".js-clear-btn");
    const openBtn = filtersMenu?.querySelector<HTMLButtonElement>(".js-open-menu-btn");
    const cancelBtn = filtersMenu?.querySelector<HTMLButtonElement>(".js-cancel-btn");
    const applyBtn = filtersMenu?.querySelector<HTMLButtonElement>(".js-apply-btn");
    const mobileFilterItems = filtersMenu?.querySelectorAll<HTMLDivElement>(".js-mobile-filter-item");
    const mobileFilterBtns = filtersMenu?.querySelectorAll<HTMLButtonElement>(".js-mobile-filter-btn");
    const readTimeLabel = resultsWrapper?.getAttribute("data-read-time-label") || "";
    const category = blogIndex.getAttribute("data-category") || "";
    if (category) {
      queryStringHandler.replace({ cat: category });
    }
    let page: number = 1;
    let totalPages: number = 0;
    let cat: string = category ?? "";
    let savedCat = "";
    let savedPage = 0;
    let results: ResultItem[] = [];
    let disabledFacets: string[] = [];

    const handleCreateBlogCard = (item: ResultItem) => {
      const cardUrl = item.url != null && item.url !== "" ? item.url : "#";
      const titleAria = item.title != null && item.title !== "" ? ` aria-label="${item.title.replace(/"/g, "&quot;")}"` : "";
      const imageLinkAria = item.title != null && item.title !== "" ? ` aria-label="${item.title.replace(/"/g, "&quot;")}"` : ` aria-label="Read article"`;
      const categoryBlock = item.categoryPage?.categoryName != null && item.categoryPage.categoryName !== "" ? (item.categoryPage.url ? `<a href="${item.categoryPage.url}" aria-label="${item.categoryPage.categoryName.replace(/"/g, "&quot;")}" class="eyebrow text-primary-600 hover:no-underline">${item.categoryPage.categoryName}</a>` : `<span class="eyebrow text-primary-600">${item.categoryPage.categoryName}</span>`) : "";
      const imageInner = item.featuredImage?.url != null && item.featuredImage.url !== "" ? `<img src="${item.featuredImage.url}" alt="${item.featuredImage.altText != null ? item.featuredImage.altText.replace(/"/g, "&quot;") : ""}" class="absolute inset-0 h-full w-full object-cover" height="180" width="320" loading="lazy" />` : "";
      const imageBlock = `<a href="${cardUrl}"${imageLinkAria} class="relative block w-full shrink-0 overflow-hidden rounded-xl aspect-video focus-visible:shadow-focus hover:no-underline">${imageInner}</a>`;
      const titleBlock = item.title != null && item.title !== "" ? `<h3 class="line-clamp-4 text-xl font-normal leading-6 text-primary-600 lg:text-2xl lg:leading-7">${item.title}</h3>` : "";
      const readingBlock =
        item.readingTime != null && item.readingTime !== ""
          ? `<span class="flex h-3 w-3 shrink-0 items-center justify-center" aria-hidden="true">
              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none" class="text-neutral-500">
                <path d="M6 3V6H8.25M10.5 6C10.5 8.48528 8.48528 10.5 6 10.5C3.51472 10.5 1.5 8.48528 1.5 6C1.5 3.51472 3.51472 1.5 6 1.5C8.48528 1.5 10.5 3.51472 10.5 6Z" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </span>
            <span>${item.readingTime} ${readTimeLabel}</span>`
          : "";
      const typeBlock = item.contentTypeDisplay != null && item.contentTypeDisplay !== "" ? `<div class="shrink-0">${item.contentTypeDisplay}</div>` : "";

      return `
        <article class="js-result-item col-span-1 flex h-full min-h-0 flex-col">
          <div class="flex min-h-0 flex-1 flex-col gap-5 rounded-3xl border border-neutral-100 bg-white p-5 global-transition">
            ${imageBlock}
            ${categoryBlock}
            <a href="${cardUrl}"${titleAria} class="flex min-h-0 flex-1 flex-col text-inherit hover:no-underline">
              ${titleBlock}
              <div class="mt-auto flex items-start justify-start gap-2.5 pt-5 eyebrow text-neutral-500">
                <div class="flex flex-1 items-center gap-1">
                  ${readingBlock}
                </div>
                ${typeBlock}
              </div>
            </a>
          </div>
        </article>
      `;
    };

    const handleNewCards = () => {
      if (results.length > 0) {
        blogIndex.setAttribute("data-noresults", "false");
        resultsWrapper?.setAttribute("data-results", "true");

        results.forEach((item: ResultItem) => {
          resultsList?.insertAdjacentHTML("beforeend", handleCreateBlogCard(item));
        });
      } else {
        blogIndex.setAttribute("data-noresults", "true");
      }
    };

    const handleRemoveCards = () => {
      blogIndex.setAttribute("data-results", "false");
      const resultItems = resultsList?.querySelectorAll<HTMLElement>(".js-result-item");

      if (resultItems && resultItems.length > 0) {
        resultItems.forEach((item) => item.remove());
      }
    };

    const handleUpdateSelectValue = (selectedQuery: string, value: string) => {
      selectFilters.forEach((item) => {
        const menuBtnSelectedText = item.querySelector<HTMLSpanElement>(".js-selected-text");
        const groupQuery = item.getAttribute("data-query");
        const filterBtns = item.querySelectorAll<HTMLButtonElement>(".js-filter-btn");

        if (filterBtns && filterBtns.length > 0 && menuBtnSelectedText && groupQuery === selectedQuery.trim()) {
          let selectedIndex = 0;

          filterBtns.forEach((btn, index) => {
            if (btn.getAttribute("data-value") === value) {
              menuBtnSelectedText.innerText = btn.textContent || "";
              selectedIndex = index;
            }
          });

          filterBtns[selectedIndex].disabled = true;
        }
      });

      mobileFilterItems?.forEach((item) => {
        const menuBtnSelectedText = item.querySelector<HTMLSpanElement>(".js-selected-mobile-text");
        const groupQuery = item.getAttribute("data-group-query");
        const filterBtns = item.querySelectorAll<HTMLButtonElement>(".js-mobile-filter-btn");

        if (menuBtnSelectedText && filterBtns && filterBtns.length > 0 && groupQuery === selectedQuery.trim()) {
          let selectedIndex = 0;

          filterBtns.forEach((btn, index) => {
            if (btn.getAttribute("data-value") === value) {
              menuBtnSelectedText.innerText = btn.textContent || "";
              selectedIndex = index;
            }
          });

          filterBtns[selectedIndex].disabled = true;
        }
      });
    };

    const handleFetchResults = async (pg: number, cat: string) => {
      try {
        let url = "";
        if (category && category !== "") {
          url = `/api/search/blog-detail-category?pg=${pg}&cat=${encodeURIComponent(cat)}`;
        } else {
          url = `/api/search/blog-detail?pg=${pg}&cat=${encodeURIComponent(cat)}&exc=${excContentId}`;
        }
        const response = await fetch(url);

        if (!response.ok) {
          throw new Error(`Request failed with status ${response.status}`);
        }

        const data = await response.json();
        // clear results array
        results = [];
        handleRemoveCards();

        if (data.items && data.items.length > 0) {
          results = data.items;
          blogIndex.setAttribute("data-noresults", "false");
        } else {
          blogIndex.setAttribute("data-noresults", "true");
        }

        if (data.facetsCollections && data.facetsCollections.length > 0) {
          handleDisabledFacetBtns();
          handleUpdateSelectValue("cat", cat);
          handleUpdateClearBtn();
        }

        totalPages = data.totalPages;

        handleNewCards();
        handleDisabledPagination();
        blogIndex.setAttribute("data-loading", "false");
      } catch (error) {
        console.error("Error during GET request:", error);
        blogIndex.setAttribute("data-noresults", "true");
        blogIndex.setAttribute("data-loading", "false");
      }
    };

    const handleUpdateQuery = (query: string, value: string) => {
      queryStringHandler.replace({ [query]: value });

      if (query === "cat") {
        cat = value;
      }
      // reset to first page on category change
      if (page !== 1 && query === "cat") {
        page = 1;
        queryStringHandler.replace({ pg: "1" });
      }

      handleFetchResults(page, cat);
    };

    const handleDisabledPagination = () => {
      if (prevPageBtn) {
        if (page === 1) {
          prevPageBtn.disabled = true;
        } else {
          prevPageBtn.disabled = false;
        }
      }

      if (nextPageBtn) {
        if (page === totalPages) {
          nextPageBtn.disabled = true;
        } else {
          nextPageBtn.disabled = false;
        }
      }
    };

    const handleClearFilters = () => {
      cat = "";
      page = 1;
      queryStringHandler.replace({ cat: "" });
      queryStringHandler.replace({ pg: "1" });

      handleFetchResults(page, cat);
    };

    const handleScrollToTop = () => {
      scrollToElement(blogIndex);
    };

    const handlePreviousPage = async () => {
      page--;
      handleUpdateQuery("pg", page.toString());
      await handleFetchResults(page, cat);
      handleScrollToTop();
    };

    const handleNextPage = async () => {
      page++;
      handleUpdateQuery("pg", page.toString());
      await handleFetchResults(page, cat);
      handleScrollToTop();
    };
    // MOBILE MENU METHODS
    const handleSetSavedData = () => {
      savedCat = cat;
      savedPage = page;
    };

    const handleOpenMenu = () => {
      if (filtersMenu && fixedMenuWrapper) {
        filtersMenu.setAttribute("data-open", "true");
        document.body.style.overflow = "hidden";
        fixedMenuWrapper.style.height = `${window.innerHeight}px`;
        handleSetSavedData();
      }
    };

    const handleCloseMenu = () => {
      if (filtersMenu && fixedMenuWrapper) {
        filtersMenu.setAttribute("data-open", "false");
        fixedMenuWrapper.style.height = "";
        document.body.style.overflow = "";
      }
    };

    const handleApplyChanges = () => {
      handleCloseMenu();
    };

    const handleResize = () => {
      if (filtersMenu && filtersMenu.getAttribute("data-open") === "true") {
        handleCloseMenu();
      }
    };

    const handleCancelChanges = () => {
      handleCloseMenu();
      if (savedCat !== cat || savedPage !== page) {
        queryStringHandler.replace({ cat: savedCat });
        queryStringHandler.replace({ pg: savedPage });
        page = savedPage;

        handleFetchResults(savedPage, savedCat);
      }
    };

    const handleDisabledFacetBtns = () => {
      if (selectFilterBtns && selectFilterBtns.length > 0) {
        selectFilterBtns.forEach((btn) => {
          const text = btn.innerText;
          if (disabledFacets.includes(text.trim())) {
            btn.disabled = true;
          } else {
            btn.disabled = false;
          }
        });
      }
      if (mobileFilterBtns && mobileFilterBtns.length > 0) {
        mobileFilterBtns.forEach((btn) => {
          const text = btn.innerText;
          if (disabledFacets.includes(text.trim())) {
            btn.disabled = true;
          } else {
            btn.disabled = false;
          }
        });
      }
    };

    const handleUpdateClearBtn = () => {
      const catQuery: string = queryStringHandler.getValue("cat");
      let showClearBtn: string = "false";

      if (catQuery) {
        showClearBtn = "true";
      }

      clearBtns.forEach((btn) => {
        btn.setAttribute("data-show", showClearBtn);
      });
    };

    mobileFilterItems?.forEach((item) => {
      const selectedText = item.querySelector<HTMLSpanElement>(".js-selected-mobile-text");
      const filterBtns = item.querySelectorAll<HTMLButtonElement>(".js-mobile-filter-btn");
      const accordionBtn = item.querySelector<HTMLButtonElement>(".js-accordion-btn");
      const accordionContentWrapper = item.querySelector<HTMLDivElement>(".js-accordion-content-wrapper");

      const handleCloseAccordion = () => {
        if (accordionBtn && accordionContentWrapper) {
          accordionBtn.setAttribute("aria-expanded", "false");
          accordionContentWrapper.style.maxHeight = "";
        }
      };

      filterBtns.forEach((btn) => {
        const groupQuery = btn.getAttribute("data-query");
        const queryValue = btn.getAttribute("data-value");

        btn.addEventListener("click", () => {
          if (selectedText) {
            selectedText.innerText = btn.textContent || "";
          }
          if (groupQuery) {
            handleUpdateQuery(groupQuery, queryValue || "");
            handleCloseAccordion();
          }
        });
      });
    });

    if (prevPageBtn) {
      prevPageBtn.addEventListener("click", handlePreviousPage);
    }

    if (nextPageBtn) {
      nextPageBtn.addEventListener("click", handleNextPage);
    }

    if (clearBtns && clearBtns.length > 0) {
      clearBtns.forEach((btn) => {
        btn.addEventListener("click", handleClearFilters);
      });
    }

    if (selectFilters) {
      selectFilters.forEach((item) => {
        const menuBtn = item.querySelector<HTMLButtonElement>(".js-select-menu-btn");
        const menuBtnSelectedText = menuBtn?.querySelector<HTMLSpanElement>(".js-selected-text");
        const filterBtns = item.querySelectorAll<HTMLButtonElement>(".js-filter-btn");
        const groupQuery = item.getAttribute("data-query");

        const handleTabIndex = () => {
          filterBtns.forEach((btn) => {
            if (item.getAttribute("data-open") === "true") {
              btn.tabIndex = 0;
            } else {
              btn.tabIndex = -1;
            }
          });
        };

        const handleToggleMenu = () => {
          if (item.getAttribute("data-open") === "false") {
            item.setAttribute("data-open", "true");
            handleTabIndex();
          } else {
            item.setAttribute("data-open", "false");
            handleTabIndex();
          }
        };

        const handleUpdateSelectedItem = (value: string) => {
          if (menuBtnSelectedText) {
            menuBtnSelectedText.innerText = value;
          }
        };

        filterBtns.forEach((btn, i) => {
          const value = btn.getAttribute("data-value");
          const text = btn.textContent;

          if (groupQuery && text) {
            btn.addEventListener("click", () => {
              handleUpdateSelectedItem(text);
              handleToggleMenu();
              handleUpdateQuery(groupQuery, value || "");
            });

            btn.addEventListener("keydown", (e: KeyboardEvent) => {
              if (e.code === "Escape") {
                item.setAttribute("data-open", "false");
                menuBtn?.focus();
                handleTabIndex();
              }
            });
          }

          if (i === filterBtns.length - 1) {
            btn.addEventListener("keydown", (e: KeyboardEvent) => {
              if (e.code === "Tab" && !e.shiftKey) {
                item.setAttribute("data-open", "false");
                handleTabIndex();
              }
            });
          }
        });

        if (menuBtn) {
          menuBtn.addEventListener("click", handleToggleMenu);
        }

        document.addEventListener("click", (e: MouseEvent) => {
          const target = e.target as Node;
          if (!item.contains(target) && item.getAttribute("data-open") === "true") {
            item.setAttribute("data-open", "false");
            handleTabIndex();
          }
        });
      });
    }
    // mobile menu actions
    if (openBtn) {
      openBtn.addEventListener("click", handleOpenMenu);
    }

    if (cancelBtn) {
      cancelBtn.addEventListener("click", handleCancelChanges);
    }

    if (applyBtn) {
      applyBtn.addEventListener("click", handleApplyChanges);
    }

    document.addEventListener("resize", handleResize);

    // set values if they're in the query string on load
    document.addEventListener("DOMContentLoaded", () => {
      const pageQuery: number = queryStringHandler.getValue("pg") ? parseInt(queryStringHandler.getValue("pg")) : page;
      const catQuery: string = queryStringHandler.getValue("cat") || cat;

      // on fetch for results if there is a query param that has filters
      if (pageQuery !== page || catQuery !== cat) {
        if (pageQuery !== page) {
          page = pageQuery;
        }

        if (catQuery !== cat) {
          cat = catQuery;
        }

        handleFetchResults(page, cat);
      } else {
        handleDisabledPagination();
        blogIndex.setAttribute("data-loading", "false");
      }
    });
  }
}
